import type { ScheduleRow, ScheduleSummaryTotals } from '../types/schedule';
import { UL_RISK_TYPES } from '../constants/tradingConstants';
import { getTodayIST } from './dateUtils';

/**
 * Derive day-level totals from a Positions tab flat list — same shape as
 * `computeBasketTotals` produces from strategy summaries, so the same
 * ProHeroBanner can render the KPI strip for the Positions view.
 *
 * Field mapping:
 *   leg_count       = total rows (each position is one leg overlay)
 *   open_count      = OPEN + EXIT_PENDING
 *   closed_count    = CLOSED
 *   filled          = entry_status === COMPLETE
 *   pending         = entry_status ∈ {TRIGGER_PENDING, OPEN, PENDING_PLACE}
 *   rejected        = entry_status === REJECTED
 *   cancelled       = entry_status === CANCELLED
 *   realized_pnl    = sum(pnl) over CLOSED positions
 *   unrealized_pnl  = sum(pnl) over OPEN / EXIT_PENDING / ENTRY_PENDING
 *   total_pnl       = realized + unrealized
 *   sl_placed       = sl_status ∈ {TRIGGER_PENDING, OPEN}
 *   sl_rejected     = sl_status === REJECTED
 *   sl_pending      = empty / CANCELLED sl_status on live positions with
 *                     stop_loss_config.enabled (mirrors _tally_sl_bucket)
 *
 * Note: the Positions endpoint already filters out synthesized scheduled-
 * not-yet-fired rows server-side, so there's no `scheduled` count to
 * compute — ProHeroBanner reads that from its own `pending` field.
 */
export function computePositionsTotals(
  positions: ScheduleRow[],
): ScheduleSummaryTotals {
  const t: ScheduleSummaryTotals = {
    leg_count: 0,
    open_count: 0,
    closed_count: 0,
    filled: 0,
    pending: 0,
    rejected: 0,
    cancelled: 0,
    realized_pnl: 0,
    unrealized_pnl: 0,
    total_pnl: 0,
    sl_placed: 0,
    sl_rejected: 0,
    sl_pending: 0,
  };
  const today = getTodayIST();

  for (const p of positions) {
    t.leg_count += 1;

    // Status buckets.
    const ps = p.position_status;
    if (ps === 'CLOSED') {
      t.closed_count += 1;
      if (typeof p.pnl === 'number') t.realized_pnl += p.pnl;
    } else if (ps === 'OPEN' || ps === 'EXIT_PENDING') {
      t.open_count += 1;
      if (typeof p.pnl === 'number') t.unrealized_pnl += p.pnl;
    } else if (ps === 'ENTRY_PENDING') {
      // No market exposure yet, no P&L contribution.
    }

    // Entry status — distinct from position status. Position can be CLOSED
    // with entry_status COMPLETE; we still count that "complete" entry.
    const es = p.entry_status;
    if (es === 'COMPLETE') t.filled += 1;
    else if (es === 'REJECTED') t.rejected += 1;
    else if (es === 'CANCELLED') t.cancelled += 1;
    // PENDING_PLACE (Phase 6, claim-then-place in flight) folds into pending
    // to match the SL bucket at line 82 below — the entry isn't live at the
    // broker yet. Backend `_tally_entry_status` groups them identically so
    // the Positions KPI strip stays consistent with the strategy view.
    // UL_MONITOR_PENDING (Phase 5b) — UL-W&T invisible-wait with no broker
    // order; folded into pending for the same reason (armed, awaiting).
    else if (es === 'TRIGGER_PENDING' || es === 'OPEN' || es === 'PENDING_PLACE' || es === 'UL_MONITOR_PENDING') t.pending += 1;

    // SL bucket — mirrors the backend _tally_sl_bucket. The frontend type
    // for `stop_loss_config` is `{type, value, reinstate_overnight?} | null`
    // (the backend's `_normalize_sl_config` strips the `enabled` flag during
    // serialization), so a non-null config IS the "enabled" signal here.
    const slEnabled = p.stop_loss_config != null;
    // UL SL never rests a broker stop (the spot monitor enforces it), so it
    // has no placed/pending state to count — mirrors the backend exemption.
    const isUlSl = slEnabled && UL_RISK_TYPES.includes(p.stop_loss_config!.type);
    const isLive = ps === 'OPEN' || ps === 'EXIT_PENDING';
    if (isLive && slEnabled && !isUlSl) {
      const sl = p.sl_status;
      if (sl === 'REJECTED') {
        t.sl_rejected += 1;
      } else if (sl === 'TRIGGER_PENDING' || sl === 'OPEN') {
        t.sl_placed += 1;
      } else if (!sl || sl === 'CANCELLED' || sl === 'PENDING_PLACE') {
        // Empty (never placed) or CANCELLED (EOD-cancelled overnight) or
        // PENDING_PLACE (sl_placement_service claim-then-place in flight at
        // backend — ~1-3 sec window, will backfill to TRIGGER_PENDING) —
        // all mean "SL not yet live at broker". Backend folds these into
        // the same pending bucket; mirror that semantic here so the schedule
        // view doesn't silently under-count during the claim window.
        // An overnight carry with the SL "Overnight" box unticked is never
        // re-placed (backend should_skip_overnight_sl → not_needed).
        const entered = (p.entry_filled_at || '').slice(0, 10) || p.execution_date || '';
        const skipOvernight = p.stop_loss_config!.reinstate_overnight === false && entered < today;
        if (!skipOvernight) t.sl_pending += 1;
      }
    }
  }

  t.total_pnl = t.realized_pnl + t.unrealized_pnl;
  return t;
}
